import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Icon } from '../icons/Icon';
import { theme } from '../../styles/theme';
import { Currency } from '../../types/currency';
import styles from './LoanTypeSection.module.less';
import { InstantAvailability } from '../../utils/checkInstantAvailability';
import { getInstantAvailabilityMessage } from '../../utils/getInstantAvailabilityMessage';

interface InstantLoanCardProps {
  selected: boolean;
  instantRate?: string;
  instantAvailability: InstantAvailability;
  currency: Currency;
  onSelect: () => void;
  isPulsing: boolean;
}

export const InstantLoanCard: React.FC<InstantLoanCardProps> = ({
  selected,
  instantRate,
  instantAvailability,
  currency,
  onSelect,
  isPulsing,
}) => {
  const intl = useIntl();

  const isAvailable = instantAvailability.available;

  const handleCardClick = () => {
    if (isAvailable) {
      onSelect();
    }
  };

  const formatRate = useCallback(
    (rate: string | undefined): string => {
      if (!rate) {
        return '';
      }
      const num = parseFloat(rate);
      if (isNaN(num)) {
        return rate;
      }
      return String(num);
    },
    []
  );

  const disabledMessage = getInstantAvailabilityMessage(
    instantAvailability,
    currency,
    intl
  );

  const cardClassName = `${styles.loanTypeCard}${!isAvailable ? ` ${styles.disabled}` : ''}${isPulsing ? ` ${styles.pulsing}` : ''}`;
  const checkboxClassName = `${styles.cardCheckbox}${selected ? ` ${styles.checked}` : ''}`;
  const checkmarkClassName = `${styles.checkmarkIcon}${selected ? ` ${styles.visible}` : ''}`;

  return (
    <div
      className={cardClassName}
      onClick={handleCardClick}
      role="button"
      tabIndex={isAvailable ? 0 : -1}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      aria-disabled={!isAvailable}
    >
      <div className={styles.cardHeader}>
        <div className={checkboxClassName}>
          <span className={checkmarkClassName}>
            <Icon type="checkmark" color={theme.white} />
          </span>
        </div>
        <div className={styles.cardInfo}>
          <div className={styles.cardTitleRow}>
            <div className={styles.cardTitle}>
              {intl.formatMessage({ id: 'loanType.instant.title' })}
            </div>
            <div className={styles.cardBadge}>
              {intl.formatMessage({ id: 'loanType.instant.timeframe' })}
              <Icon
                type="bolt"
                color={
                  isAvailable
                    ? theme.palette.slate[400]
                    : theme.palette.slate[500]
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.cardDescription}>
        {isAvailable
          ? intl.formatMessage({ id: 'loanType.instant.description' })
          : disabledMessage && (
              <>
                {disabledMessage.title && (
                  <>
                    <span className={styles.unavailableTitle}>
                      {disabledMessage.title}
                    </span>
                    <br />
                  </>
                )}
                <span className={styles.unavailableReason}>
                  {disabledMessage.content}
                </span>
              </>
            )}
      </div>

      {isAvailable && instantRate && (
        <div className={styles.instantRateDisplay}>
          <span>{formatRate(instantRate)}</span>
          <span>{intl.formatMessage({ id: 'interestRate.display.suffix' })}</span>
        </div>
      )}
    </div>
  );
};
